import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, MapPin, Grid3X3, Box, Package, ChevronLeft, ChevronRight, X, Filter, Pencil, Plus } from "lucide-react";
import { Button } from "./components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Edit Location Modal ─── */
function EditLocationModal({ item, racks, bins, onClose, onSaved }) {
  const [newRackId, setNewRackId] = useState(item.rackId || "");
  const [newBinId, setNewBinId] = useState(item.binId || "");
  const [saving, setSaving] = useState(false);

  const filteredBins = bins.filter(b => !newRackId || b.rackId === newRackId);

  const handleSave = async () => {
    if (!newRackId || !newBinId) { toast.error("Select both rack and bin"); return; }
    if (newRackId === item.rackId && newBinId === item.binId) { toast.info("No changes made"); onClose(); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/rack-bin/update-location`, {
        item_code: item.itemCode,
        old_rack_id: item.rackId,
        old_bin_id: item.binId,
        new_rack_id: newRackId,
        new_bin_id: newBinId,
      });
      toast.success(`Moved ${data.modified} entries to ${data.new_rack} / ${data.new_bin}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update location");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="edit-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" data-testid="edit-modal">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Pencil className="w-4 h-4 text-orange-600" /> Update Location</h3>
          <p className="text-xs text-slate-400 mt-0.5">{item.itemName} ({item.itemCode})</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-slate-50 rounded-lg px-4 py-3 text-xs text-slate-600">
            <span className="font-semibold">Current:</span> Rack <span className="font-bold text-purple-700">{item.rackName}</span> / Bin <span className="font-bold text-orange-700">{item.binName}</span>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">New Rack</label>
            <select value={newRackId} onChange={e => { setNewRackId(e.target.value); setNewBinId(""); }}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              data-testid="edit-rack-select">
              <option value="">Select Rack</option>
              {racks.map(r => <option key={r.id} value={r.id}>{r.name} ({r.department})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">New Bin</label>
            <select value={newBinId} onChange={e => setNewBinId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
              data-testid="edit-bin-select">
              <option value="">Select Bin</option>
              {filteredBins.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="edit-save">
            {saving ? "Saving..." : "Update Location"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Add Location Modal ─── */
function AddLocationModal({ racks, bins, onClose, onSaved }) {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rackId, setRackId] = useState("");
  const [binId, setBinId] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const filteredBins = bins.filter(b => !rackId || b.rackId === rackId);

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/api/rack-bin/item-search`, { params: { q: search } });
        setSearchResults(data.items || []);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSave = async () => {
    if (!selectedItem) { toast.error("Select an item first"); return; }
    if (!rackId || !binId) { toast.error("Select both rack and bin"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/rack-bin/add-location`, {
        item_code: selectedItem.itemCode,
        rack_id: rackId,
        bin_id: binId,
      });
      if (data.inserted > 0) {
        toast.success(`Added ${data.inserted} entries to ${data.rack} / ${data.bin}`);
      } else {
        toast.info(`Item already exists at ${data.rack} / ${data.bin} (${data.already_existed} entries)`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add location");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="add-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" data-testid="add-modal">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-600" /> Add Product Location</h3>
          <p className="text-xs text-slate-400 mt-0.5">Assign a rack and bin to a product</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Item Search */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Search Item</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input value={search} onChange={e => { setSearch(e.target.value); setSelectedItem(null); }}
                placeholder="Type item name or code..."
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                data-testid="add-item-search" />
            </div>
            {searching && <p className="text-xs text-slate-400 mt-1">Searching...</p>}
            {!selectedItem && searchResults.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-sm">
                {searchResults.map(it => (
                  <button key={it.itemCode} onClick={() => { setSelectedItem(it); setSearch(it.itemName); setSearchResults([]); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 border-b border-slate-50 last:border-0"
                    data-testid={`search-result-${it.itemCode}`}>
                    <span className="font-medium text-slate-800">{it.itemName}</span>
                    <span className="ml-2 text-slate-400 font-mono">{it.itemCode}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedItem && (
              <div className="mt-2 bg-emerald-50 rounded-lg px-3 py-2 text-xs flex items-center justify-between">
                <span><span className="font-semibold text-emerald-800">{selectedItem.itemName}</span> <span className="text-emerald-600 font-mono">({selectedItem.itemCode})</span></span>
                <button onClick={() => { setSelectedItem(null); setSearch(""); }} className="text-emerald-600 hover:text-emerald-800"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Rack</label>
            <select value={rackId} onChange={e => { setRackId(e.target.value); setBinId(""); }}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
              data-testid="add-rack-select">
              <option value="">Select Rack</option>
              {racks.map(r => <option key={r.id} value={r.id}>{r.name} ({r.department})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Bin</label>
            <select value={binId} onChange={e => setBinId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
              data-testid="add-bin-select">
              <option value="">Select Bin</option>
              {filteredBins.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="add-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !selectedItem} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-save">
            {saving ? "Saving..." : "Add Location"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function RackBinLocation() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ racks: [], bins: [], departments: [] });
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({ search: "", rack: "", bin_name: "", department: "", show_zero: false });
  const [editItem, setEditItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchData = useCallback(async (pg) => {
    setLoading(true);
    try {
      const params = { page: pg || page, page_size: 50 };
      if (filters.search) params.search = filters.search;
      if (filters.rack) params.rack = filters.rack;
      if (filters.bin_name) params.bin_name = filters.bin_name;
      if (filters.department) params.department = filters.department;
      if (filters.show_zero) params.show_zero = true;
      const { data: res } = await axios.get(`${API}/api/rack-bin/locations`, { params });
      setData(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 0);
      if (pg) setPage(pg);
    } catch (e) { console.error("Error fetching rack-bin data:", e); }
    setLoading(false);
  }, [page, filters]);

  const fetchFilters = useCallback(async () => {
    try {
      const [filtersRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/rack-bin/filters`),
        axios.get(`${API}/api/rack-bin/summary`),
      ]);
      setFilterOptions(filtersRes.data);
      setSummary(summaryRes.data);
    } catch (e) { console.error("Error fetching filters:", e); }
  }, []);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchData(1); }, [filters]);
  useEffect(() => { fetchData(); }, [page]);

  const clearFilters = () => { setFilters({ search: "", rack: "", bin_name: "", department: "", show_zero: false }); setPage(1); };
  const hasFilters = filters.search || filters.rack || filters.bin_name || filters.department || filters.show_zero;

  const handleSaved = () => { setEditItem(null); setShowAddModal(false); fetchData(page); fetchFilters(); };

  const racksByDept = filterOptions.racks.reduce((acc, r) => {
    const dept = r.department || "Other";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rack-bin-page">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/pharmacy")} data-testid="back-to-pharmacy"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-orange-600" /> Rack & Bin Location</h1>
              <p className="text-xs text-slate-400">{total} product locations (item level)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowAddModal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-location-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Location
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(f => !f)} data-testid="toggle-filters" className={showFilters ? "bg-slate-100" : ""}>
              <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-4 space-y-4">
        {/* KPI Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="rack-bin-summary">
            {[
              { label: "Total Locations", value: summary.total_locations, icon: MapPin, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "With Stock", value: summary.with_stock, icon: Package, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Empty Locations", value: summary.empty_locations, icon: Box, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Total Racks", value: summary.total_racks, icon: Grid3X3, color: "text-purple-600", bg: "bg-purple-50" },
              { label: "Total Bins", value: summary.total_bins, icon: Grid3X3, color: "text-orange-600", bg: "bg-orange-50" },
            ].map((c, i) => (
              <div key={i} className={`${c.bg} rounded-xl px-4 py-3 border border-slate-100`} data-testid={`kpi-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <c.icon className={`w-3.5 h-3.5 ${c.color}`} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</span>
                </div>
                <span className="text-xl font-bold text-slate-800">{c.value?.toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid="rack-bin-filters">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Item Name</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                    placeholder="Search item..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-orange-500/10 focus:border-orange-300"
                    data-testid="filter-search" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Rack</label>
                <select value={filters.rack} onChange={e => { setFilters(f => ({ ...f, rack: e.target.value })); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white" data-testid="filter-rack">
                  <option value="">All Racks</option>
                  {Object.entries(racksByDept).map(([dept, rks]) => (
                    <optgroup key={dept} label={dept}>
                      {rks.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Bin</label>
                <select value={filters.bin_name} onChange={e => { setFilters(f => ({ ...f, bin_name: e.target.value })); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white" data-testid="filter-bin">
                  <option value="">All Bins</option>
                  {filterOptions.bins.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Department</label>
                <select value={filters.department} onChange={e => { setFilters(f => ({ ...f, department: e.target.value })); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white" data-testid="filter-department">
                  <option value="">All Departments</option>
                  {filterOptions.departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={filters.show_zero} onChange={e => { setFilters(f => ({ ...f, show_zero: e.target.checked })); setPage(1); }}
                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500" data-testid="filter-show-zero" />
                  Show empty locations
                </label>
              </div>
            </div>
            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500" data-testid="clear-filters">
                  <X className="w-3 h-3 mr-1" /> Clear Filters
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">{total} items | Page {page} of {totalPages || 1}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Rack</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Bin</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Name</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Code</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Category</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Department</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Total Qty</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Batches</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">UoM</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">MRP</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={11} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
                )}
                {!loading && data.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center py-12 text-sm text-slate-400">No locations found</TableCell></TableRow>
                )}
                {!loading && data.map((item, i) => (
                  <TableRow key={`${item.itemCode}-${item.rackId}-${item.binId}-${i}`} className="hover:bg-slate-50 transition-colors" data-testid={`loc-row-${i}`}>
                    <TableCell className="text-xs">
                      <span className="inline-flex px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold text-[10px]">{item.rackName}</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="inline-flex px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-medium text-[10px]">{item.binName}</span>
                    </TableCell>
                    <TableCell className="text-xs font-medium text-slate-800 max-w-[220px] truncate">{item.itemName || "-"}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{item.itemCode || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{item.category || "-"}</TableCell>
                    <TableCell className="text-xs">{item.department || "-"}</TableCell>
                    <TableCell className="text-xs text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${item.totalQty === 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {item.totalQty}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-center font-medium text-slate-600">{item.batchCount}</TableCell>
                    <TableCell className="text-xs">{item.unitOfMeasure || "-"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">
                      {item.mrp ? `\u20B9${item.mrp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={() => setEditItem(item)} className="h-7 w-7 p-0 text-slate-400 hover:text-orange-600" data-testid={`edit-btn-${i}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-xs font-semibold text-slate-600 px-2">{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {editItem && <EditLocationModal item={editItem} racks={filterOptions.racks} bins={filterOptions.bins} onClose={() => setEditItem(null)} onSaved={handleSaved} />}
      {showAddModal && <AddLocationModal racks={filterOptions.racks} bins={filterOptions.bins} onClose={() => setShowAddModal(false)} onSaved={handleSaved} />}
    </div>
  );
}
